<template>
  <div class="relative -mx-16">
    <JMap
      :fullscreenControl="false"
      :zoomControl="false"
      class="overflow-hidden"
      :markers="markers"
      :apiKey="GOOGLE_MAP_API_KEY"
      :height="windowHeight"
      enableClustering
    />

    <div class="absolute bottom-[10rem] left-0 right-0 mx-auto w-full px-12">
      <JButton block size="lg" @click="onBack">
        {{ $t('search.backToResults') }}
      </JButton>
    </div>
  </div>

  <JBottomSheet v-model="isOpenSelectedHotelBottomSheet" hasPopstate>
    <div class="pb-8">
      <div
        class="mx-8 cursor-pointer rounded-md border border-line bg-gray-100 p-8 pb-8"
        @click="setHotelDetailIsOpen(true)"
      >
        <div class="flex justify-between gap-12">
          <div class="flex flex-col gap-8">
            <div>
              <JRating
                :modelValue="selectedHotelFromMap?.hotelRate"
                size="12"
                class="!gap-x-4"
              />
            </div>
            <h6 class="font-bold">
              {{ selectedHotelFromMap?.name }}
            </h6>
            <div class="flex items-center gap-x-4 text-gray-500">
              <JIcon name="location" size="12" />
              <span class="text-metadata-limited font-medium text-gray-500">
                {{ selectedHotelFromMap?.destinationName }}
              </span>
            </div>
            <div
              :class="{ invisible: !selectedHotelFromMap?.breakfastIncluded }"
              class="w-max rounded-sm border border-line px-2 text-metadata-limited"
            >
              {{ $t('search.breakfastIncluded') }}
            </div>
          </div>
          <div
            v-if="!selectedHotelFromMap?.breakfastIncluded"
            class="rounded-sm border border-line p-4 text-metadata-limited"
          >
            <span>
              {{ $t('search.breakfastIncluded') }}
            </span>
          </div>
          <JImage
            :src="hotelImage"
            alt="Hotel"
            containerClass="w-full aspect-square rounded-md h-max !w-[10rem]"
            class="object-cover"
          />
        </div>
        <div class="mt-8 flex">
          <h6 class="font-bold">
            {{ price }}
          </h6>
        </div>
      </div>
    </div>
  </JBottomSheet>

  <JBottomSheet
    v-model="hotelDetailIsOpen"
    fullScreen
    hasPopstate
    @update:model-value="onHotelDetailUpdate"
  >
    <template #header>
      <JContainer class="flex justify-center px-16">
        <TheMobileHeader
          showBack
          :title="$t('book.hotelDetails')"
          titleClass="font-bold text-base flex-[10] px-16"
          @back="closeHotelDetail"
        />
      </JContainer>
    </template>

    <ServerError v-if="isError" @retry="refetch" />

    <HotelDetail
      v-else
      showAvailability
      :isLoading="hotelInfoIsFetching"
      @select="closeHotelDetail"
      @reserve="closeHotelDetail"
    />
  </JBottomSheet>
</template>

<script lang="ts" setup>
import {
  JBottomSheet,
  JButton,
  JContainer,
  JIcon,
  JImage,
  JMap,
  JRating,
} from '@journey/ui-kit'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToggle } from '@vueuse/core'

import TheMobileHeader from '@/modules/Core/components/layout/MobileHeader/TheMobileHeader.vue'
import ServerError from '@/modules/Core/components/shared/ServerError/ServerError.vue'
import HotelDetail from '@/modules/Core/components/shared/HotelDetail/HotelDetail.vue'
import type { Hotel } from '@/modules/Search/types/model/search'
import useHotelSearchResultsStore from '@/modules/Search/store/hotelSearchResults'
import useSearchPanelHistoryStore from '@/modules/Search/store/searchPanelHistory'
import { useHotelInfoQuery } from '@/modules/Search/requests/queries'
import { HotelImageSize } from '@/modules/Search/types'
import { GOOGLE_MAP_API_KEY } from '@/modules/Search/constants'
import featureFlag from '@/config/featureFlag'
import { useCurrency } from '@/modules/Core/composables'

const router = useRouter()

const hotelSearchResultStore = useHotelSearchResultsStore()
const searchPanelHistory = useSearchPanelHistoryStore()

const searchOption = ref(0)
const customerCode = ref(featureFlag.customerCode)
const searchId = computed(() => searchPanelHistory.history.searchId ?? '')

const [hotelDetailIsOpen, setHotelDetailIsOpen] = useToggle(false)
const {
  data,
  isFetching: hotelInfoIsFetching,
  isError,
  refetch,
} = useHotelInfoQuery(searchId, searchOption, customerCode, {
  enabled: hotelDetailIsOpen,
})

watch(data, (value) => {
  if (!value) {
    return
  }

  if (!value.data) {
    return
  }

  hotelSearchResultStore.setSelectedHotel(value.data)
})

const markers = computed(() => {
  return hotelSearchResultStore.hotelsList.map(
    ({ latitude, longitude, searchOption }) => ({
      position: {
        lat: Number.parseFloat(latitude),
        lng: Number.parseFloat(longitude),
      },
      onClick: () => openHotelDetail(searchOption),
      icon: '/marker.png',
    })
  )
})

const windowHeight = computed(() => window.innerHeight)

function onBack() {
  router.push({ name: 'Result' })
}

function clearRooms() {
  hotelSearchResultStore.rooms = []
}

function closeHotelDetail() {
  clearRooms()
  setHotelDetailIsOpen(false)
}

const selectedHotelFromMap = ref<Hotel>()
const [isOpenSelectedHotelBottomSheet, setOpenSelectedHotelBottomSheet] =
  useToggle(false)

function openHotelDetail(option: number) {
  selectedHotelFromMap.value = hotelSearchResultStore.hotelsList.find(
    (hotel) => hotel.searchOption === option
  )

  if (!selectedHotelFromMap.value) {
    return
  }

  searchOption.value = option

  setOpenSelectedHotelBottomSheet(true)
}

function onHotelDetailUpdate(value: boolean) {
  if (value) {
    return
  }

  clearRooms()
}

const hotelImage = computed(() => {
  if (!selectedHotelFromMap.value) {
    return ''
  }

  const { images } = selectedHotelFromMap.value

  if (!images.length) {
    return ''
  }

  const biggerImage = images[0].pathList.find(
    (img) => img.size === HotelImageSize.Bigger
  )?.address

  const mediumImage = images[0].pathList.find(
    (img) => img.size === HotelImageSize.Medium
  )?.address

  return biggerImage || mediumImage || ''
})

const { formatCurrency } = useCurrency()
const price = computed(() =>
  formatCurrency(
    selectedHotelFromMap.value?.price,
    selectedHotelFromMap.value?.convertedCurrency
  )
)
</script>
